import { requestApi } from 'api/api'
import dayjs from 'dayjs'
import { DateFormat } from 'constants/General'
import { CsvExportTransaction, PdfExportTransaction, Transaction } from 'types/transaction'
import { CustomDateRangeReq } from 'types/transaction/CustomDateRangeReq'
import {
  GetWalletTransactionsReq,
  WalletTransactionsExport,
} from 'types/transaction/GetTransactionsReq'
import { TransactionsPagination } from 'types/transaction/TransactionsPagination'
import type { ExportFileFormatType, ReportType } from 'types/walletTransactionsExport'

export type transactionExportFileFormatType = 'csv' | 'pdf'

export interface BuildWalletExportPayloadParams {
  reportType: ReportType
  selectedExportFormat: ExportFileFormatType
  walletLSID?: string | number
  selectedDateRange?: { from: string; to: string }
  balanceDate?: string
}

export const buildWalletExportPayload = ({
  reportType,
  selectedExportFormat,
  walletLSID,
  selectedDateRange,
  balanceDate,
}: BuildWalletExportPayloadParams): WalletTransactionsExport => {
  const isBalanceReport = reportType === 'BALANCE'

  return {
    selectedExportFormat,
    walletId: walletLSID,
    reportType,
    ...(isBalanceReport
      ? {
          balanceDate: balanceDate
            ? dayjs(balanceDate, DateFormat.DD_COMMA_MM_COMMA_YYYY).format(DateFormat.YYYY_MM_DD) +
              'T23:59:59'
            : undefined,
        }
      : {
          selectedDateRange: {
            from: `${selectedDateRange?.from}T00:00:00`,
            to: `${selectedDateRange?.to}T23:59:59`,
          },
        }),
  }
}

export const getWalletTransactions = async ({
  walletId,
  offset,
  max,
}: GetWalletTransactionsReq): Promise<TransactionsPagination> => {
  const { data, headers }: { data: Transaction[]; headers: { [key: string]: string } } =
    await requestApi({
      url: `/transaction/${walletId}`,
      method: 'GET',
      params: {
        ...(!!offset && { offset }),
        ...(!!max && { max }),
      },
      fullResponse: true,
    })
  return {
    transactions: data || [],
    paging: {
      total: Number(headers['x-api-list-total']),
      offset: Number(headers['x-api-list-start']),
      max,
    },
  }
}

export const sendTransactionExportToEmail = ({
  selectedExportFormat,
  walletId,
  selectedDateRange,
  isV2Enabled = false,
}: WalletTransactionsExport): Promise<CsvExportTransaction[] | PdfExportTransaction[]> => {
  const requestUrl: Record<transactionExportFileFormatType, string> = {
    csv: isV2Enabled ? '/transaction/v2/report' : '/transaction/report',
    pdf: isV2Enabled ? '/transaction/v2/report/external' : '/transaction/report/external',
  }
  return requestApi({
    url: requestUrl?.[selectedExportFormat],
    params: {
      walletId,
      format: selectedExportFormat,
      fromDate: selectedDateRange?.from,
      toDate: selectedDateRange?.to,
    },
    method: 'GET',
  })
}

export const sendTransactionExportToEmailAllWallets = (
  selectedDateRange?: CustomDateRangeReq
): Promise<Transaction[]> => {
  return requestApi({
    url: '/transaction/report',
    params: {
      format: 'csv',
      fromDate: selectedDateRange?.from,
      toDate: selectedDateRange?.to,
    },
    method: 'GET',
  })
}

export const sendTransactionsReport = ({
  selectedExportFormat,
  walletId,
  selectedDateRange,
  reportType = 'TRANSACTION',
  balanceDate,
}: WalletTransactionsExport): Promise<void> => {
  const dateParams =
    reportType === 'BALANCE'
      ? { at: balanceDate }
      : { from: selectedDateRange?.from, to: selectedDateRange?.to }

  return requestApi({
    baseUrl: process.env.API_URL,
    url: '/ledger/v1/reports',
    data: {
      ...dateParams,
      format: selectedExportFormat,
      deliveryChannel: 'EMAIL',
      type: reportType,
      ...(!!walletId && { walletId }),
    },
    method: 'POST',
  })
}
